import type { Entity } from '@api/api/entity-schema';
import type { SnakeMeta } from '@api/api/timeseries-schema';
import type { QueryClient } from '@tanstack/react-query';
import { createEntityQueryOptions } from '@terminal/react-query-hooks/entities/useEntities.utils';
import { createSnakeMetaQueryOptions } from '@terminal/react-query-hooks/snakes/useSnake';
import { fetchTheme } from '@terminal/services/overview-widget/overview-widget-service';
import { withGcTime } from '@terminal/services/query-client/utils';

import {
  createThemeChartEntity,
  createThemeChartSnakeMeta,
  isThemeChartTag,
  isThemePerformanceSnake,
  parseThemeIdFromChartTag,
  parseThemeIdFromPerformanceSnake,
  type ThemeChartIdentity,
} from '../../utils/chart-theme-asset/chartThemeAsset';

interface ChartQueryOptions {
  queryClient: QueryClient;
  gcTime?: number;
}

export const fetchThemeChartIdentity = async (
  themeId: string
): Promise<ThemeChartIdentity | undefined> => {
  try {
    const theme = await fetchTheme({ themeId });

    return theme
      ? { id: theme.id, name: theme.name, logo_url: theme.logo_url }
      : undefined;
  } catch {
    return undefined;
  }
};

interface FetchChartEntitiesProps extends ChartQueryOptions {
  tags: string[];
}

export const fetchChartEntities = async ({
  tags,
  queryClient,
  gcTime,
}: FetchChartEntitiesProps): Promise<Entity[]> => {
  const entities = await Promise.all(
    tags.map(async tag => {
      if (isThemeChartTag(tag)) {
        const identity = await fetchThemeChartIdentity(
          parseThemeIdFromChartTag(tag)
        );

        return identity ? createThemeChartEntity(identity) : undefined;
      }

      const queryOptions = withGcTime(createEntityQueryOptions(tag), gcTime);
      const response = await queryClient.query({
        ...queryOptions,
        staleTime: 'static',
      });

      if (!response.data) {
        queryClient.removeQueries({
          queryKey: queryOptions.queryKey,
          exact: true,
        });
        return undefined;
      }

      return response.data;
    })
  );

  return entities.filter((entity): entity is Entity => entity !== undefined);
};

interface FetchChartSnakeMetaProps extends ChartQueryOptions {
  snake: string;
}

export const fetchChartSnakeMeta = ({
  snake,
  queryClient,
  gcTime,
}: FetchChartSnakeMetaProps): Promise<SnakeMeta | undefined> => {
  if (isThemePerformanceSnake(snake)) {
    return fetchThemeChartIdentity(
      parseThemeIdFromPerformanceSnake(snake)
    ).then(identity =>
      identity ? createThemeChartSnakeMeta(identity) : undefined
    );
  }

  return queryClient
    .query({
      ...withGcTime(createSnakeMetaQueryOptions(snake), gcTime),
      staleTime: 'static',
    })
    .catch(() => undefined);
};
